import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

/** Series configuration for multi-line charts */
export interface LineSeries {
  dataKey: string;
  name: string;
  color: string;
  strokeDasharray?: string;
  dot?: boolean;
}

export interface LineChartWrapperProps {
  /** Chart data array */
  data: Record<string, unknown>[];
  /** X-axis data key */
  xAxisKey: string;
  /** Line series definitions */
  series: LineSeries[];
  /** Chart height in pixels */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Y-axis domain */
  yDomain?: [number | 'auto', number | 'auto'];
  /** Custom tooltip formatter */
  tooltipFormatter?: (value: ValueType | undefined, name: NameType | undefined) => [React.ReactNode, NameType] | React.ReactNode;
}

const GRID_COLOR = '#EFEFEF';
const AXIS_TICK_COLOR = '#555555';

/**
 * Recharts LineChart wrapper with standard OUTFRONT styling.
 */
export function LineChartWrapper({
  data,
  xAxisKey,
  series,
  height = 300,
  xAxisLabel,
  yAxisLabel,
  yDomain,
  tooltipFormatter,
}: LineChartWrapperProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
        <XAxis
          dataKey={xAxisKey}
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          tickLine={false}
          axisLine={{ stroke: GRID_COLOR }}
          label={
            xAxisLabel
              ? { value: xAxisLabel, position: 'insideBottom', offset: -5, fontSize: 12, fill: AXIS_TICK_COLOR }
              : undefined
          }
        />
        <YAxis
          tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
          tickLine={false}
          axisLine={false}
          domain={yDomain}
          label={
            yAxisLabel
              ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 12, fill: AXIS_TICK_COLOR }
              : undefined
          }
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DCDCDC',
            borderRadius: 4,
            fontSize: 13,
          }}
          formatter={tooltipFormatter}
        />
        <Legend
          verticalAlign="top"
          iconType="circle"
          wrapperStyle={{ paddingBottom: 16, fontSize: 11, fontWeight: 600 }}
        />
        {series.map((s) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            strokeDasharray={s.strokeDasharray}
            dot={s.dot !== false ? { r: 4, fill: s.color } : false}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default LineChartWrapper;
